import { cn } from "@bcc/lib/utils";
import type { FlagCountryCode } from "@shared/types";

/**
 * Country codes that have a flag SVG asset under `public/images/flags/`. Kept
 * in lockstep with the shared `FlagCountryCode` type and the assets the
 * language switcher (`user-nav.tsx`) already renders — add the SVG and the code
 * here together when a new ship-to destination is enabled.
 */
const FLAG_ASSET_COUNTRIES: readonly FlagCountryCode[] = ["AU", "GB", "US", "DE", "NL"];

/**
 * Builds the public URL of a flag SVG asset. Exported so tests and stories
 * assert against this single source of truth instead of hardcoding the
 * `/images/flags/XX.svg` path, which silently rots if the assets ever move.
 */
export const setFlagUrl = (countryCode: FlagCountryCode): string => `/images/flags/${countryCode}.svg`;

const hasFlagAsset = (code: string): code is FlagCountryCode => (FLAG_ASSET_COUNTRIES as readonly string[]).includes(code);

interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code (e.g. "NL", "DE"). Unknown/unsupported codes fall back to a neutral circle. */
  countryCode: string;
  /** Diameter of the circular flag in pixels. Defaults to 20. */
  size?: number;
  className?: string;
  testId?: string;
  /**
   * Accessible text for the flag. Defaults to the raw country code, which is
   * all most callers can offer — pass translated copy (e.g. "Netherlands flag")
   * where the surrounding UI has a country name to hand.
   */
  alt?: string;
}

/**
 * Renders a small circular country flag from the shared SVG assets in
 * `public/images/flags/` — the same assets the language switcher uses, keyed by
 * `FlagCountryCode`. Reusing them keeps a single flag convention across BCC and
 * supports every shipped flag (AU/GB/US/DE/NL), not just simple tricolours.
 * Unknown country codes fall back to a neutral circle showing the 2-letter code
 * so the UI never breaks on an unmapped destination.
 */
export const CountryFlag = ({ countryCode, size = 20, className, testId, alt }: CountryFlagProps) => {
  const dimension = { width: size, height: size };
  const label = alt ?? countryCode;
  const normalized = countryCode.toUpperCase();

  if (!hasFlagAsset(normalized)) {
    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        data-testid={testId}
        className={cn(
          "inline-flex items-center justify-center rounded-full border border-border bg-muted text-[8px] font-semibold text-muted-foreground",
          className
        )}
        style={dimension}
      >
        {normalized.slice(0, 2)}
      </span>
    );
  }

  return (
    <img
      src={setFlagUrl(normalized)}
      alt={label}
      title={label}
      data-testid={testId}
      width={size}
      height={size}
      className={cn("inline-block rounded-full border border-border object-cover", className)}
      style={dimension}
    />
  );
};
